/**
 * UnsavedChangesDialog - Warning dialog for dirty state tracking
 *
 * Displays when a user attempts to navigate away or close a form
 * with unsaved changes, providing options to save, discard, or cancel.
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

/**
 * Props for the UnsavedChangesDialog component
 */
export interface UnsavedChangesDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when user chooses to discard changes */
  onDiscard: () => void;
  /** Callback when user chooses to save changes */
  onSave: () => void;
  /** Callback when user cancels and returns to editing */
  onCancel: () => void;
}

/**
 * UnsavedChangesDialog Component
 *
 * A confirmation dialog that appears when the user has unsaved changes
 * and attempts to navigate away. Provides three options:
 * - Save: Save changes before leaving
 * - Discard: Discard changes and leave
 * - Cancel: Return to editing
 */
export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  isOpen,
  onDiscard,
  onSave,
  onCancel,
}) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Would you like to save them before leaving?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onDiscard}>
            Discard
          </Button>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button onClick={onSave}>Save</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
